import {type BoardColumn, useBoardLabels} from '@github-ui/board-core'
import {VisuallyHidden} from '@primer/react'
import {clsx} from 'clsx'
import type {ReactNode} from 'react'

import styles from './BoardColumnHeader.module.css'

/**
 * Which heading element a name renders as. The frames may be embedded anywhere in a
 * caller's page, so the caller picks the level that fits its outline rather than the
 * board imposing one. A native element is used rather than `role="heading"` with
 * `aria-level`, which has only partial assistive-technology support.
 */
export type BoardHeadingLevel = 2 | 3 | 4 | 5 | 6

export type BoardColumnHeaderProps = {
  column: BoardColumn
  headingLevel?: BoardHeadingLevel
  /** An overflow menu, or anything else that belongs beside the name. */
  trailing?: ReactNode
  className?: string
}

/**
 * A column's name and its count.
 *
 * The name is a real heading, so a board of many columns can be skimmed by heading
 * rather than read through.
 *
 * The count is `column.totalCount` and nothing else. It is the server's number for the
 * whole column: summing the counts of the cells below it would be wrong on a swimlaned
 * board, where those cells page independently and may not all be loaded, and counting
 * the cards it holds would be wrong everywhere.
 *
 * It is drawn as a bare number and announced with the unit it is counting. Beside a
 * heading, "812" is read as belonging to it; announced on its own it is a number with
 * nothing attached, so the reader's copy is the label set's rather than the digits.
 */
export function BoardColumnHeader({column, headingLevel = 3, trailing, className}: BoardColumnHeaderProps) {
  const Heading = `h${headingLevel}` as const
  const labels = useBoardLabels()

  return (
    <div className={clsx(styles.ColumnHeader, className)}>
      <Heading className={styles.Name}>{column.label}</Heading>
      {column.totalCount === undefined ? null : (
        <span className={styles.Count}>
          {/*
            The digits are drawn and the wording is announced, nested rather than side by
            side so point-and-read lands on both: a pointer on the badge would otherwise
            find only the hidden node and say nothing. `aria-label` is not an option here,
            because a span carries no role for one to attach to.
          */}
          <span aria-hidden="true">{column.totalCount}</span>
          <VisuallyHidden>{labels.columnCount(column.totalCount)}</VisuallyHidden>
        </span>
      )}
      {trailing === undefined ? null : <span className={styles.Trailing}>{trailing}</span>}
    </div>
  )
}
